import type { ReactElement } from 'react';
import { useState } from 'react';

import { useHideVacancyByModeratorMutation } from '@/api/vacancyComplaintsApi';
import { Modal } from '@/components/Modal';
import { Textarea } from '@/components/Textarea';

const MODERATOR_REASON_MAX_LENGTH = 500;

interface HideVacancyModalProps {
    isOpen: boolean;
    complaintId: string;
    onClose: () => void;
    onResolved: () => void;
    onError: (message: string) => void;
}

/**
 * Confirm-модал действия «Скрыть вакансию» (FR-1.16): ввод причины модератора
 * для кафе (≤500, обязательна) → `POST /vacancy-complaints/:id/hide-vacancy`.
 * Текст модала — нормативный (design-spec §2.5). После успеха — `onResolved`
 * (UI перерисует статус по обновлённой жалобе из инвалидации тега).
 */
export function HideVacancyModal({
    isOpen,
    complaintId,
    onClose,
    onResolved,
    onError
}: HideVacancyModalProps): ReactElement {
    const [moderatorReason, setModeratorReason] = useState('');
    const [reasonError, setReasonError] = useState<string | null>(null);
    const [hideVacancy, { isLoading: isHiding }] = useHideVacancyByModeratorMutation();

    const close = (): void => {
        setModeratorReason('');
        setReasonError(null);
        onClose();
    };

    const submit = async (): Promise<void> => {
        const reasonTrimmed = moderatorReason.trim();
        if (reasonTrimmed === '') {
            setReasonError('Укажите причину для кафе');
            return;
        }
        if (reasonTrimmed.length > MODERATOR_REASON_MAX_LENGTH) {
            setReasonError(`Не более ${MODERATOR_REASON_MAX_LENGTH} символов`);
            return;
        }
        setReasonError(null);
        try {
            await hideVacancy({
                id: complaintId,
                body: { moderatorReason: reasonTrimmed }
            }).unwrap();
            setModeratorReason('');
            onResolved();
        } catch {
            onError('Не удалось скрыть вакансию');
            close();
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={close}
            title="Скрыть вакансию"
            testId="vacancy-complaint-hide-modal"
        >
            <div className="space-y-4" data-testid="vacancy-complaint-hide-form">
                <p className="text-sm text-gray-600">
                    Вакансия получит статус «Скрыта модератором», исчезнет из каталога; кафе
                    получит уведомление с причиной.
                </p>
                <Textarea
                    label="Причина для кафе (обязательно)"
                    value={moderatorReason}
                    onChange={(e) => setModeratorReason(e.target.value)}
                    rows={5}
                    maxLength={MODERATOR_REASON_MAX_LENGTH}
                    error={reasonError ?? undefined}
                    data-testid="vacancy-complaint-hide-reason"
                    autoFocus
                />
                <div className="text-right text-xs text-gray-400">
                    {moderatorReason.length} / {MODERATOR_REASON_MAX_LENGTH}
                </div>
                <div className="flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={close}
                        className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 transition hover:bg-gray-100"
                    >
                        Отмена
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            void submit();
                        }}
                        disabled={isHiding}
                        data-testid="vacancy-complaint-hide-submit"
                        className="rounded bg-red-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-red-700 disabled:opacity-50"
                    >
                        Скрыть вакансию
                    </button>
                </div>
            </div>
        </Modal>
    );
}
